import cn from 'classnames';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../UI/Button/Button';
import { Checkbox, RegistrationCheckbox } from '../../UI/Checkbox/Checkbox';
import { HidePasswordIcon, VisiblePasswordIcon } from '../../assets/icons';
import { scrollAvailable } from '../../helpers/scrollToggler';
import { useAppDispatch } from '../../hooks/reduxHooks/useAppDispatch';
import { PATH_DASHBOARD } from '../../routes/RouterConfig';
import { registerStudent } from '../../services/Register';
import styles from './Register.module.scss';
import { getStudentRegisterSchema } from '../../validations/studentValidation';


interface StudentFormValues {
    first_name: string;
    second_name: string;
    document_number: string;
    email: string;
    password: string;
    confirm_password: string;
}

const StudentRegisterForm: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [isAgreeWith, setIsAgreeWith] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

    const initialValues: StudentFormValues = {
        first_name: '',
        second_name: '',
        document_number: '',
        email: '',
        password: '',
        confirm_password: '',
    };

    const formik = useFormik<StudentFormValues>({
        initialValues,
        validationSchema: getStudentRegisterSchema(),
        validateOnMount: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await dispatch(registerStudent(values));
                navigate(PATH_DASHBOARD.root);
                scrollAvailable();
            } catch (error) {
                toast.error('Произошла ошибка сервера');
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <form autoComplete="off" onSubmit={formik.handleSubmit}>
            <div className={styles.inputs__wrapper}>
                <input
                    type="text"
                    className={styles.input}
                    placeholder="Имя"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.first_name}
                    name="first_name"
                    style={{
                        borderColor: formik.touched.first_name && formik.errors.first_name ? 'red' : formik.values.first_name ? 'green' : 'grey',
                        background: 'inherit',
                    }}
                />

                <input
                    type="text"
                    className={styles.input}
                    placeholder="Фамилия"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.second_name}
                    name="second_name"
                    style={{
                        borderColor: formik.touched.second_name && formik.errors.second_name ? 'red' : formik.values.second_name ? 'green' : 'grey',
                        background: 'inherit',
                    }}
                />

                <label className={styles.input__label}>
                    <input
                        type="text"
                        className={styles.input}
                        placeholder="Номер студенческого билета или диплома"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.document_number}
                        name="document_number"
                        style={{
                            borderColor:
                                formik.touched.document_number && formik.errors.document_number
                                    ? 'red'
                                    : formik.values.document_number
                                        ? 'green'
                                        : 'grey',
                            background: 'inherit',
                        }}
                    />
                    {formik.touched.document_number && formik.errors.document_number && (
                        <p className={styles.input__error}>{formik.errors.document_number}</p>
                    )}
                </label>

                <input
                    type="email"
                    className={styles.input}
                    placeholder="Email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    name="email"
                    style={{
                        borderColor: formik.touched.email && formik.errors.email ? 'red' : formik.values.email ? 'green' : 'grey',
                        background: 'inherit',
                    }}
                />

                <label className={styles.input__label}>
                    <div className={styles.input__password}>
                        <input
                            type={isPasswordVisible ? 'text' : 'password'}
                            className={cn(styles.input, styles.password__input)}
                            placeholder="Пароль"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            name="password"
                            style={{
                                borderColor: formik.touched.password && formik.errors.password ? 'red' : formik.values.password ? 'green' : 'grey',
                                background: 'inherit',
                            }}
                        />
                        <div className={styles.password__icon} onClick={() => setIsPasswordVisible((s) => !s)}>
                            {isPasswordVisible ? <VisiblePasswordIcon /> : <HidePasswordIcon />}
                        </div>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                        <p className={styles.input__error}>{formik.errors.password}</p>
                    )}
                </label>

                <label className={styles.input__label}>
                    <div className={styles.input__password}>
                        <input
                            type={isConfirmPasswordVisible ? 'text' : 'password'}
                            className={styles.input}
                            placeholder="Повторите пароль"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.confirm_password}
                            name="confirm_password"
                            style={{
                                borderColor:
                                    formik.touched.confirm_password && formik.errors.confirm_password
                                        ? 'red'
                                        : formik.values.confirm_password
                                            ? 'green'
                                            : 'grey',
                                background: 'inherit',
                            }}
                        />
                        <div className={styles.password__icon} onClick={() => setIsConfirmPasswordVisible((s) => !s)}>
                            {isConfirmPasswordVisible ? <VisiblePasswordIcon /> : <HidePasswordIcon />}
                        </div>
                    </div>
                    {formik.touched.confirm_password && formik.errors.confirm_password && (
                        <p className={styles.input__error}>{formik.errors.confirm_password}</p>
                    )}
                </label>
            </div>

            <RegistrationCheckbox
                text="Я ознакомлен/а с политикой конфиденциальности и согласен на обработку персональных данных"
                className={styles.inputs__checkbox}
                isChecked={isAgreeWith}
                onChange={() => setIsAgreeWith((v) => !v)}
            />

            <Checkbox text="Запомнить пароль" className={styles.inputs__checkbox} />

            <div className={styles.inputs__buttons}>
                <Button
                    isDisabled={!formik.isValid || !formik.dirty || !isAgreeWith}
                    buttonType="submit"
                    className={cn(styles.inputs__button, {
                        [styles.inputs__button_valid]: formik.dirty && formik.isValid && isAgreeWith,
                    })}
                >
                    Создать профиль
                </Button>
            </div>
        </form>
    );
};

export default StudentRegisterForm;
